import { lazy, Suspense, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './lib/ThemeContext';
import { AuthProvider } from './lib/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ParticleBackground from './components/ParticleBackground';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Lazy load pages
const HomePage = lazy(() => import('./pages/HomePage'));
const SolutionsPage = lazy(() => import('./pages/SolutionsPage'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const ManagedServicesPage = lazy(() => import('./pages/ManagedServicesPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const DevOpsPage = lazy(() => import('./pages/DevOpsPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-[#2a4b9b] border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  );
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="relative z-10 min-h-screen">
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function AdminLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="relative z-10 min-h-screen">
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="relative min-h-screen">
            <ParticleBackground />
              <Routes>
                <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
                <Route path="/solutions" element={<PublicLayout><SolutionsPage /></PublicLayout>} />
                <Route path="/industries" element={<PublicLayout><IndustriesPage /></PublicLayout>} />
                <Route path="/managed-services" element={<PublicLayout><ManagedServicesPage /></PublicLayout>} />
                <Route path="/partners" element={<PublicLayout><PartnersPage /></PublicLayout>} />
                <Route path="/company" element={<PublicLayout><CompanyPage /></PublicLayout>} />
                <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                <Route path="/devops" element={<PublicLayout><DevOpsPage /></PublicLayout>} />
                <Route path="/admin" element={<AdminLayout><AdminPage /></AdminLayout>} />
                <Route path="/admin/login" element={<AdminLayout><AdminPage /></AdminLayout>} />
                <Route path="*" element={
                  <PublicLayout>
                    <div className="min-h-screen flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-6xl font-bold text-muted-foreground mb-4">404</h1>
                        <p className="text-muted-foreground mb-6">Page not found</p>
                        <a href="/" className="text-sm text-[#2a4b9b] hover:underline">Go back home</a>
                      </div>
                    </div>
                  </PublicLayout>
                } />
              </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
